import { ChangeEvent, useContext, useEffect, useState } from 'react';
import { AppContext } from '../../../context';
import { Modal } from '../BaseModal';
import { EmployeeNodeModel } from '../../../../models';
import { useGetAllowancesList } from '../../../hooks';
import { AllowanceModel } from '../../../../models/allowances/allowanceModel';

interface BonusModalProps {
    isOpen: boolean;
    onClose: () => void;
    startDate: string | number | undefined;
    endDate: string | number | undefined;
    employee: EmployeeNodeModel[];
    saveBonusAssignments: (
        bonusRuleIds: number[],
        startDate: string | number | undefined,
        endDate: string | number | undefined,
        employees: EmployeeNodeModel[]
    ) => void;
}

export const BonusModal = ({
    isOpen,
    onClose,
    startDate,
    endDate,
    employee,
    saveBonusAssignments,
}: BonusModalProps) => {
    const { langResources, setIsOpenBonusModal, activeSkinConfig } = useContext(AppContext);

    const {
        allowancesList,
        allowancesListCopy,
        isLoadingDataList,
        setAllowancesListCopy,
    } = useGetAllowancesList();

    const [searchTerm, setSearchTerm] = useState('');
    const [bonusSelected, setBonusSelected] = useState<number>(-1);
    const [selectedBonus, setSelectedBonus] = useState<AllowanceModel | undefined>();
    const [selectedRuleIds, setSelectedRuleIds] = useState<number[]>([]);

    useEffect(() => {
        if (bonusSelected !== -1) {
            const bonus = allowancesList.find((b) => b.id === bonusSelected);
            setSelectedBonus(bonus);
            setSelectedRuleIds([]);
        } else {
            setSelectedBonus(undefined);
            setSelectedRuleIds([]);
        }
    }, [bonusSelected, allowancesList]);

    const handlerChangeBonus = (e: ChangeEvent<HTMLInputElement>) => {
        let bonusName = e.target.value;
        setSearchTerm(bonusName);

        const bonusFiltered = allowancesList.filter(
            (b) =>
                b.name.toLowerCase().includes(bonusName.toLowerCase()) ||
                b.description.toLowerCase().includes(bonusName.toLowerCase())
        );

        setAllowancesListCopy(bonusFiltered);
    };

    const handlerCloseModal = () => {
        window.localStorage.removeItem('selection-range');
        setIsOpenBonusModal(false);
        setBonusSelected(-1);
        setSearchTerm('');
        onClose();
    };

    const handleResetSearchTerm = () => {
        setSearchTerm('');
        setAllowancesListCopy(allowancesList);
    };

    const handleRuleCheck = (ruleId: number) => {
        setSelectedRuleIds((prev) =>
            prev.includes(ruleId)
                ? prev.filter((id) => id !== ruleId)
                : [...prev, ruleId]
        );
    };

    const handlerSelectBonus = (id: number) => {
        setBonusSelected(id);
    };

    const handlerSubmit = () => {
        saveBonusAssignments(selectedRuleIds, startDate, endDate, employee);
        handlerCloseModal();
    };

    return (
        <Modal
            isOpen={isOpen}
            modalTitle={langResources['bonus']['modal']['title']}
            onClose={handlerCloseModal}
            onSubmit={handlerSubmit}
            disableSubmit={bonusSelected === -1 || selectedRuleIds.length === 0}
        >
            <div className='modal-body' style={{ paddingBottom: 0, minHeight: 450 }}>
                <div className='row'>
                    {!isLoadingDataList ? (
                        <div className='col-3'>
                            <div className='mb-3'>
                                <input
                                    type='text'
                                    className='form-control'
                                    placeholder={langResources['bonus']['modal']['search.placeholder']}
                                    value={searchTerm}
                                    onChange={handlerChangeBonus}
                                />
                                {searchTerm && (
                                    <button
                                        className='btn btn-sm btn-link'
                                        onClick={handleResetSearchTerm}
                                    >
                                        {langResources['bonus']['modal']['clear.button']}
                                    </button>
                                )}
                            </div>

                            <div
                                style={{
                                    maxHeight: 380,
                                    minHeight: 250,
                                    overflowY: 'scroll',
                                }}
                            >
                                <ul className='list-group list-group-flush'>
                                    {allowancesListCopy.map((bonus) => (
                                        <li
                                            onClick={() => handlerSelectBonus(bonus.id)}
                                            className={`list-group-item p-2 px-3 text-selected ${bonusSelected === bonus.id &&
                                                'text-selected--active'
                                                }`}
                                            key={bonus.id}
                                            title={bonus.name}
                                            style={{
                                                cursor: 'pointer',
                                            }}
                                        >
                                            <strong>{bonus.typeDescription}</strong> - {bonus.name}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    ) : (
                        <div className='col-3'>
                            <h5 className='card-title placeholder-glow'>
                                <span className='placeholder col-6'></span>
                            </h5>
                            <p className='card-text placeholder-glow'>
                                <span className='placeholder col-12 bg-dark'></span>
                            </p>
                            <p className='card-text placeholder-glow'>
                                <span className='placeholder col-7 bg-dark'></span>
                            </p>
                            <p className='card-text placeholder-glow'>
                                <span className='placeholder col-12 bg-dark'></span>
                            </p>
                        </div>
                    )}

                    <div className='col-9'>
                        <div className='row'>
                            <div className='col-12'>
                                <h5
                                    className='mb-3'
                                    style={{ color: activeSkinConfig.principalColor }}
                                >
                                    {langResources['bonus']['modal']['details.title']}
                                </h5>
                            </div>

                            {selectedBonus ? (
                                <>
                                    <div className='col-12 mb-3'>
                                        <div className='card'>
                                            <div className='card-body'>
                                                <h6 className='card-title'>
                                                    {selectedBonus.name}
                                                </h6>
                                                <p className='card-text text-muted'>
                                                    <strong>{langResources['bonus']['modal']['type.label']}</strong>{' '}
                                                    {selectedBonus.typeDescription}
                                                </p>
                                                <p className='card-text'>
                                                    <strong>{langResources['bonus']['modal']['description.label']}</strong>{' '}
                                                    {selectedBonus.description}
                                                </p>
                                                <p className='card-text'>
                                                    <strong>{langResources['bonus']['modal']['concept.label']}</strong>{' '}
                                                    {selectedBonus.concept}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className='col-12'>
                                        <h6
                                            className='mb-2'
                                            style={{ color: activeSkinConfig.principalColor }}
                                        >
                                            {langResources['bonus']['modal']['rules.title']}
                                        </h6>
                                        {selectedBonus.bonusRules && selectedBonus.bonusRules.length > 0 ? (
                                            <div className='list-group'>
                                                {selectedBonus.bonusRules.map((rule) => (
                                                    <label key={rule.id} className='list-group-item d-flex align-items-center'>
                                                        <input
                                                            type='checkbox'
                                                            className='form-check-input me-2'
                                                            checked={selectedRuleIds.includes(rule.id)}
                                                            onChange={() => handleRuleCheck(rule.id)}
                                                        />
                                                        <div className='flex-grow-1'>
                                                            <div className='d-flex w-100 justify-content-between'>
                                                                <h6 className='mb-1'>{rule.name}</h6>
                                                                <span className={`badge ${rule.isActive ? 'bg-success' : 'bg-danger'}`}>
                                                                    {rule.isActive
                                                                        ? langResources['bonus']['modal']['status.active']
                                                                        : langResources['bonus']['modal']['status.inactive']}
                                                                </span>
                                                            </div>
                                                            <p className='mb-1 text-muted small'>{rule.description}</p>
                                                            <div className='mt-2 small'>
                                                                {rule.isConditionalToStartOfTheWorkshift && (
                                                                    <div>
                                                                        <i className='ri-time-line'></i> {langResources['bonus']['modal']['condition.start.time']} {rule.workshiftStartTime}
                                                                    </div>
                                                                )}
                                                                {rule.isConditionalToEndOfTheWorkshift && (
                                                                    <div>
                                                                        <i className='ri-time-line'></i> {langResources['bonus']['modal']['condition.end.time']} {rule.workshiftEndTime}
                                                                    </div>
                                                                )}
                                                                {rule.isConditionalByHourRange && (
                                                                    <div>
                                                                        <i className='ri-calendar-line'></i> {langResources['bonus']['modal']['condition.hour.range']} {rule.startHourRange} - {rule.endHourRange}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </label>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className='text-muted'>
                                                {langResources['bonus']['modal']['no.rules.message']}
                                            </p>
                                        )}
                                    </div>

                                    <div className='col-12 mt-3'>
                                        <div className='alert alert-info'>
                                            <strong>{langResources['bonus']['modal']['assignment.period.title']}</strong>
                                            <br />
                                            {langResources['bonus']['modal']['assignment.from.label']} {startDate} | {langResources['bonus']['modal']['assignment.to.label']} {endDate}
                                            <br />
                                            <strong>{langResources['bonus']['modal']['assignment.employees.label']}</strong> {employee.length}
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className='col-12 text-center text-muted py-5'>
                                    <i
                                        className='ri-award-line'
                                        style={{ fontSize: '48px' }}
                                    ></i>
                                    <p className='mt-3'>
                                        {langResources['bonus']['modal']['no.selection.message']}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    );
};